// eslint-disable-next-line import/no-extraneous-dependencies
import {
  mount,
  shallow,
  ShallowRendererProps,
  ShallowWrapper,
  ReactWrapper,
} from 'enzyme';
import React, {FunctionComponentElement} from 'react';
import ReactTestUtils from 'react-dom/test-utils';
import {IntlProvider, createIntl, IntlShape, createIntlCache} from 'react-intl';
import {AnyAction} from 'redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import sinon from 'sinon';

const messages = {}; // locale messages
const defaultLocale = 'en';
const locale = defaultLocale;
const textComponent = 'span';

interface TestUtilsType {
  DUMMY_ACTION: AnyAction;
  DUMMY_ACTION_A: AnyAction;
  DUMMY_ACTION_B: AnyAction;
  AppConfigMock: {class: any; service: {url: Record<string, any>}};
  intl: IntlShape;
  chaiHelpers: (chai: Chai.ChaiStatic) => void;
  shallowWithIntl: (
    node: React.ReactElement,
    options?: ShallowRendererProps
  ) => ShallowWrapper;
  mountWithIntl: (node: React.ReactElement) => ReactWrapper;
  renderWithIntl: (
    element: FunctionComponentElement<{ref: (c: any) => any}>
  ) => any;
  thunkMiddleware: ({
    dispatch,
    getState,
  }: {
    dispatch: any;
    getState: any;
  }) => (next: sinon.SinonSpy) => (action) => sinon.SinonSpy;
  createStore: () => {
    store: {
      getState: sinon.SinonSpy;
      dispatch: sinon.SinonSpy;
    };
    next: sinon.SinonSpy;
    invoke: (action) => sinon.SinonSpy;
  };
}

const TestUtils: TestUtilsType = {
  DUMMY_ACTION: {type: 'DUMMY_ACTION'},
  DUMMY_ACTION_A: {type: 'DUMMY_ACTION_A'},
  DUMMY_ACTION_B: {type: 'DUMMY_ACTION_B'},

  AppConfigMock: {class: {}, service: {url: {}}},

  intl: createIntl({locale, defaultLocale, messages}, createIntlCache()),

  chaiHelpers(chai) {
    const {Assertion} = chai;

    Assertion.addMethod(
      'eqlActionsSomeTypeOnly',
      function (expected, typeOnlyActions) {
        const actual = this._obj;

        new Assertion(actual.length).to.eql(expected.length);
        expected.forEach((action, idx) => {
          if (typeOnlyActions.indexOf(action.type) === -1) {
            // not typeOnlyActions should equal deeply
            new Assertion(action).to.eql(actual[idx]);
          } else {
            // typeOnlyActions should equal only in type
            new Assertion(action.type).to.eql(actual[idx].type);
          }
        });
      }
    );
  },

  /**
   * Components using the react-intl module require access to the intl context.
   * This is not available when mounting single components in Enzyme.
   * These helper functions aim to address that and wrap a valid,
   * intl context around them.
   */
  shallowWithIntl(node, options = {}) {
    return shallow(node, {
      ...options,
      wrappingComponent: IntlProvider,
      wrappingComponentProps: {
        locale,
        defaultLocale,
        messages,
        textComponent,
      },
    })
      .dive()
      .dive();
  },

  mountWithIntl(node) {
    return mount(node, {
      wrappingComponent: IntlProvider,
      wrappingComponentProps: {
        locale,
        defaultLocale,
        messages,
        textComponent,
      },
    });
  },

  /* eslint-disable no-return-assign */
  renderWithIntl(element) {
    let instance;

    ReactTestUtils.renderIntoDocument(
      <IntlProvider
        locale={locale}
        messages={messages}
        textComponent={textComponent}
      >
        {React.cloneElement(element, {
          ref: (c) => (instance = c.refs.wrappedInstance),
        })}
      </IntlProvider>
    );

    return instance;
  },
  /* eslint-enable no-return-assign */

  thunkMiddleware({dispatch, getState}) {
    return (next) => (action) => {
      if (typeof action === 'function') {
        return action(dispatch, getState);
      }

      return next(action);
    };
  },

  createStore() {
    const store = {
      getState: sinon.fake(() => ({})),
      dispatch: sinon.fake(),
    };
    const next = sinon.fake();

    const invoke = (action) => TestUtils.thunkMiddleware(store)(next)(action);

    return {store, next, invoke};
  },
};

export default TestUtils;
